import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Mail, RefreshCw } from "lucide-react";

const TYPES = [
  ["signup", "Welcome email on signup (incl. Google Sign-In)"],
  ["points_added", "Points added (purchase / manual, after approval)"],
  ["redemption", "Points redeemed"],
  ["referral", "Referral points (referrer and new member)"],
];
const STATUS_STYLE = {
  sent: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
  skipped: "bg-slate-100 text-slate-600",
};

function EmailLog() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/email-log?limit=25").then(({ data }) => setRows(data))
    .catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);
  return (
    <div className="mt-5" data-testid="email-log">
      <div className="flex items-center justify-between mb-2">
        <div className="label-caps">Recent email sends</div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="email-log-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>
      <div className="overflow-x-auto rounded-xl" style={{ border: "1px solid var(--border)" }}>
        <table className="w-full text-xs">
          <thead style={{ background: "var(--bg-soft)", color: "var(--text-muted)" }}>
            <tr>
              {["Time", "Type", "Member", "To", "Status", "Detail"].map((h) => (
                <th key={h} className="text-left px-3 py-2 label-caps">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center" style={{ color: "var(--text-muted)" }}>
                No emails yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`email-log-row-${r.id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{new Date(r.timestamp).toLocaleString()}</td>
                <td className="px-3 py-2">{r.kind}</td>
                <td className="px-3 py-2 font-mono">{r.customer_id}</td>
                <td className="px-3 py-2">{r.to || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[r.status] || ""}`}>
                    {r.status}{r.attempts > 1 ? ` ×${r.attempts}` : ""}
                  </span>
                </td>
                <td className="px-3 py-2" style={{ color: "var(--text-muted)" }}>{r.detail || r.subject}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function EmailSettingsCard() {
  const { isSuperAdmin } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/email-settings").then(({ data }) => setForm(data)).catch((e) => toast.error(formatError(e)));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/email-settings", form);
      setForm(data);
      toast.success("Email settings saved");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!form) return null;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="w-card w-card-bordered p-6 mt-6" data-testid="email-settings-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-full grid place-items-center text-white bg-navy">
          <Mail className="h-4 w-4" />
        </div>
        <div>
          <h3 className="font-bold">Member email notifications</h3>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
            Turning an email off never stops the signup, credit or redemption itself.
            {!isSuperAdmin && " Only the super admin can change these settings."}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between py-2 border-b" style={{ borderColor: "var(--border)" }}>
        <span className="font-semibold text-sm">Master switch — all member emails</span>
        <Switch checked={form.master} disabled={!isSuperAdmin} onCheckedChange={set("master")}
                data-testid="email-toggle-master" />
      </div>
      {TYPES.map(([k, label]) => (
        <div key={k} className="flex items-center justify-between py-2">
          <span className="text-sm" style={{ opacity: form.master ? 1 : 0.5 }}>{label}</span>
          <Switch checked={form[k]} disabled={!isSuperAdmin} onCheckedChange={set(k)}
                  data-testid={`email-toggle-${k}`} />
        </div>
      ))}
      {isSuperAdmin && (
        <>
          <Button type="button" onClick={save} disabled={busy} className="mt-3 rounded-xl bg-navy text-white"
                  data-testid="email-settings-save-btn">
            {busy ? "Saving…" : "Save email settings"}
          </Button>
          <EmailLog />
        </>
      )}
    </div>
  );
}
